import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as documentActions from '../actions/document.actions';
import * as errorActions from '../actions/error.actions';
import * as filterActions from '../actions/filter.actions';
import { DocumentService } from '@app-root-services';
import { IDrive, IError, IFolder } from '@app-root-interfaces';
import { catchError, concatMap, filter, map, take, withLatestFrom, switchMap, tap } from 'rxjs/operators';
import { combineLatest, of, iif } from 'rxjs';
import { appReducer } from '@app-root-store';
import { Store, select } from '@ngrx/store';
import { IFolderTree } from '../../../main/interfaces/folder-tree.interface';
import * as documentSelectors from '../selectors/document.selector';
import * as userSelectors from '../selectors/user.selector';
import * as filterSelectors from '../selectors/filter.selector';
import * as profileSelectors from '../selectors/profile.selector';
import * as routerSelectors from '../selectors/router.selector';
import { Location } from '@angular/common';
import { NoFilesAvailableError } from '../../exceptions';
import { TinyHelper } from '../../../shared/helper/tiny-helper';
import { IProfile } from '@scf-monorepo/interfaces';

@Injectable()
export class DocumentEffects {
    private readonly store$ = inject(Store<appReducer.AppState>);
    private readonly actions$ = inject(Actions);
    private readonly documentService = inject(DocumentService);
    private readonly location = inject(Location);

    selectDrive$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(documentActions.selectDrive),
            withLatestFrom(this.store$.pipe(select(routerSelectors.selectUrl))),
            switchMap(([{ driveId }, url]) =>
                this.documentService.selectDrive(driveId).pipe(
                    tap(() => this.navigatePath([], url)),
                    switchMap((foundDrive: IDrive) => of(documentActions.getDriveSuccess({ drive: foundDrive }))),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'document-error-get'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        );
    });

    getDrive$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.getDrive),
            withLatestFrom(
                this.store$.pipe(select(userSelectors.selectUserIsAdmin)),
                this.store$.pipe(select(profileSelectors.selectProfileSelectedProfile))
            ),
            switchMap(([, isAdmin, profile]) => (isAdmin ? this.loadAdminDrives() : this.loadUserDrives(profile)))
        )
    );

    getFolder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.getFolder),
            concatMap(({ key }) =>
                combineLatest([
                    this.store$.pipe(select(documentSelectors.selectDocumentFolders)),
                    this.store$.pipe(select(userSelectors.selectUserIsAdmin)),
                    this.store$.pipe(select(profileSelectors.selectProfileUserProfile))
                ]).pipe(
                    filter(([folders, isAdmin, profile]) => !!folders[key]?.folders || isAdmin || !!profile),
                    take(1),
                    concatMap(([folders, isAdmin, profile]) => {
                        if (folders[key]?.folders) {
                            return of(documentActions.getFolderSuccess({ folder: folders[key] }));
                        }
                        return this.documentService
                            .getFolder(key, isAdmin, profile)
                            .pipe(map((folder: IFolder) => documentActions.getFolderSuccess({ folder })));
                    }),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'document-error-get'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    buildPathToNode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.buildPathToNode),
            withLatestFrom(
                this.store$.pipe(select(documentSelectors.selectDocumentFolderTree)),
                this.store$.pipe(select(routerSelectors.selectUrl))
            ),
            map(([{ nodeId }, tree, url]) => {
                const path = this.getTreePath(tree, nodeId, []);
                return { path, url };
            }),
            tap(({ path, url }) => this.navigatePath(path, url)),
            switchMap(({ path }) => {
                return of(documentActions.buildPathToNodeSuccess({ path }));
            })
        )
    );

    getFavourites$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.getFavourites),
            withLatestFrom(
                this.store$.pipe(select(documentSelectors.selectDocumentFiles)),
                this.store$.pipe(select(userSelectors.selectUserCurrentUser))
            ),
            concatMap(([, files, user]) =>
                this.documentService.getMissingFavourites(files, user).pipe(
                    map((favourites) => documentActions.getFavouritesSuccess({ favourites })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'document-error-favorites-get'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    getRecentlySeen$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.getRecentlySeen),
            withLatestFrom(
                this.store$.pipe(select(documentSelectors.selectDocumentFiles)),
                this.store$.pipe(select(userSelectors.selectUserCurrentUser))
            ),
            concatMap(([, files, user]) => {
                if (!user || !user.recentDocuments || !user.recentDocuments.length) {
                    return of(documentActions.getRecentlySeenSuccess({ recentlySeen: [] }));
                }

                const keys: string[] = user.recentDocuments.filter((documentId: string) => !files[documentId]);
                if (keys.length === 0) {
                    return of(documentActions.getRecentlySeenSuccess({ recentlySeen: [] }));
                }

                return this.documentService.getRecentlySeen(keys).pipe(
                    map((recentlySeen) => documentActions.getRecentlySeenSuccess({ recentlySeen })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'document-error-lastused-get'
                        };
                        return of(errorActions.addError({ error }));
                    })
                );
            })
        )
    );

    searchFiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.searchFiles),
            withLatestFrom(
                this.store$.pipe(select(filterSelectors.selectFilterFilters)),
                this.store$.pipe(select(filterSelectors.selectFilterSearchAll)),
                this.store$.pipe(select(documentSelectors.selectDocumentAllDriveIds))
            ),
            switchMap(([{ keyword }, filters, searchAll, driveIds]) =>
                this.documentService.searchFiles(keyword, filters, searchAll ? null : driveIds).pipe(
                    map((files) => documentActions.searchFilesSuccess({ files })),
                    catchError(() => {
                        return of(documentActions.searchFilesFailure());
                    })
                )
            )
        )
    );

    createFolder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.createFolder),
            withLatestFrom(
                this.store$.pipe(select(documentSelectors.selectDocumentCurrentFolder)),
                this.store$.pipe(select(documentSelectors.selectDocumentDrive))
            ),
            concatMap(([{ name }, parent, drive]) =>
                this.documentService.createFolder(name, parent, drive).pipe(
                    map((folder) => documentActions.createFolderSuccess({ folder })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'folder-error-add'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    getFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.getFile),
            concatMap(({ id }) =>
                this.documentService.getFile(id).pipe(
                    map((file) => documentActions.getFileSuccess({ file })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'file-error-get'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    // TODO error handling verbessern
    // TODO Folder bereits vorher laden um zu wissen ob sie gelöscht werden können.
    deleteFolder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.deleteFolder),
            concatMap(({ folder }) =>
                this.documentService.deleteFolder(folder).pipe(
                    map(() => documentActions.deleteFolderSuccess({ folder })),
                    catchError(() => of(documentActions.deleteFolderFailure({ folder })))
                )
            )
        )
    );

    // TODO Error handling verbessern
    patchFolder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.patchFolder),
            concatMap(({ folder }) =>
                this.documentService.patchFolder(folder).pipe(
                    map(() => documentActions.patchFolderSuccess({ folder })),
                    catchError(() => of(documentActions.patchFolderFailure({ folder })))
                )
            )
        )
    );

    deleteFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.deleteFile),
            concatMap(({ file }) =>
                this.documentService.deleteFile(file).pipe(
                    map(() => documentActions.deleteFileSuccess({ file })),
                    catchError(() => of(documentActions.deleteFileFailure({ file })))
                )
            )
        )
    );

    changeFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.changeFile),
            withLatestFrom(this.store$.pipe(select(documentSelectors.selectDocumentToEdit))),
            concatMap(([{ payload }, file]) =>
                this.documentService
                    .changeFile(file, payload)
                    .pipe(map((data) => documentActions.changeFileSuccess({ file: data })))
            )
        )
    );

    updateFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.updateFile),
            withLatestFrom(this.store$.pipe(select(documentSelectors.selectDocumentToEdit))),
            concatMap(([, file]) =>
                this.documentService.updateFile(file).pipe(
                    switchMap((data) =>
                        iif(
                            () => !!data.file,
                            of(documentActions.updateEditFile({ file: data })),
                            of(documentActions.updateEditPreview({ file: data }))
                        )
                    ),
                    catchError(() => of(documentActions.updateFileFailure()))
                )
            )
        )
    );

    updateEditFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.updateEditFile),
            withLatestFrom(this.store$.pipe(select(documentSelectors.selectDocumentDrive))),
            concatMap(([{ file }, drive]) =>
                this.documentService.updateEditFile(file, drive).pipe(
                    switchMap((data) =>
                        iif(
                            () => !!data.preview,
                            of(documentActions.updateEditPreview({ file: data })),
                            of(documentActions.updateFileSuccess({ file: data }))
                        )
                    ),
                    catchError(() => of(documentActions.updateEditFileFailure()))
                )
            )
        )
    );

    updateEditPreview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.updateEditPreview),
            withLatestFrom(this.store$.pipe(select(documentSelectors.selectDocumentDrive))),
            concatMap(([{ file }, drive]) =>
                this.documentService.updateEditPreview(file, drive).pipe(
                    map((data) => documentActions.updateFileSuccess({ file: data })),
                    catchError(() => of(documentActions.updateEditPreviewFailure()))
                )
            )
        )
    );

    deleteFileFromFolder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.deleteFileFromFolder),
            withLatestFrom(
                this.store$.pipe(select(documentSelectors.selectDocumentFolder)),
                this.store$.pipe(select(documentSelectors.selectDocumentDrive))
            ),
            concatMap(([{ file }, folder, drive]) =>
                this.documentService.deleteFileFromFolder(file, folder, drive).pipe(
                    map((data) => documentActions.deleteFileFromFolderSuccess(data)),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'file-error-delete-from-folder'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    addToPath$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.addToPath),
            withLatestFrom(
                this.store$.pipe(select(documentSelectors.selectDocumentPathRaw)),
                this.store$.pipe(select(routerSelectors.selectUrl))
            ),
            map(([{ folder }, path, url]) => ({ path: [...path, folder.id], url })),
            tap(({ path, url }) => this.navigatePath(path, url)),
            switchMap(({ path }) => of(documentActions.addToPathSuccess({ path })))
        )
    );

    removeFromPath$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.removeFromPath),
            withLatestFrom(
                this.store$.pipe(select(documentSelectors.selectDocumentPathRaw)),
                this.store$.pipe(select(routerSelectors.selectUrl))
            ),
            map(([{ elements }, path, url]) => ({
                path: path.slice(0, elements * -1),
                url
            })),
            tap(({ path, url }) => this.navigatePath(path, url)),
            switchMap(({ path }) => of(documentActions.removeFromPathSuccess({ path })))
        )
    );

    setPathBack$ = createEffect(() =>
        this.actions$.pipe(
            ofType(documentActions.setPathBack),
            withLatestFrom(
                this.store$.pipe(select(documentSelectors.selectDocumentPathRaw)),
                this.store$.pipe(select(routerSelectors.selectUrl))
            ),
            map(([, path, url]) => ({
                path: path.length > 0 ? [path[0]] : [],
                url
            })),
            tap(({ path, url }) => this.navigatePath(path, url)),
            switchMap(({ path }) => of(documentActions.setPathBackSuccess({ path })))
        )
    );

    private loadAdminDrives() {
        return this.documentService.getAllDrives().pipe(
            switchMap((drives) => of(documentActions.getAllDrivesSuccess({ drives }), filterActions.getAllFilters())),
            catchError((err) => {
                const error: IError = {
                    obj: err,
                    isCritical: false,
                    text: 'drives-error-get'
                };
                return of(errorActions.addError({ error }));
            })
        );
    }

    private loadUserDrives(profile: IProfile) {
        return this.documentService.getDrive(profile).pipe(
            switchMap((drive) =>
                of(
                    documentActions.getDriveSuccess({ drive: drive.drives }),
                    documentActions.getBasicSuccess({ folder: drive.basicFolder }),
                    documentActions.setDriveCollectionIds({ collectionIds: drive.allDriveIds }),
                    filterActions.getAllFilters()
                )
            ),
            catchError((e) => {
                if (e instanceof NoFilesAvailableError) {
                    return of(
                        errorActions.addError({ error: { obj: null, text: 'document-error-nothing-found' } }),
                        documentActions.getDriveSuccess({
                            drive: {
                                folders: [],
                                files: [],
                                isDrive: true,
                                id: TinyHelper.createTmpId('tmp-'),
                                driveCollectionIds: []
                            } as IDrive
                        }),
                        documentActions.getBasicSuccess({ folder: { files: [], isDrive: false } as IFolder }),
                        filterActions.getAllFilters()
                    );
                }
                return this.catchError('document-error-get')(e);
            })
        );
    }

    private catchError(message: string) {
        return (err) => {
            const error: IError = {
                obj: err,
                isCritical: false,
                text: message
            };
            return of(errorActions.addError({ error }));
        };
    }

    private getTreePath(tree: IFolderTree, nodeId: string, prev: string[]): string[] {
        const path = [...prev, tree.id];
        if (tree.id === nodeId) {
            return path;
        }
        if (tree.childs.length === 0) {
            return [];
        }
        const result = tree.childs.reduce((arr, node) => [...arr, ...this.getTreePath(node, nodeId, path)], []);
        return result;
    }

    private navigatePath(path: string[], url: string) {
        const pathAllIndex = url.indexOf('/main/all');
        const pathUnitIndex = url.indexOf('/main/my-unit');
        if (pathAllIndex === 0) {
            this.location.replaceState(`/main/all/${path.join(',')}`);
        } else if (pathUnitIndex === 0) {
            this.location.replaceState(`/main/my-unit/${path.join(',')}`);
        }
    }
}
